/** @module csi/toolbar/BatchFilter */
define([
	"dojo/_base/declare",
	"dojo/_base/lang",
	"dojo/dom-class",
	"dojo/promise/all",
	"dijit/_Container",
	"dijit/focus",
	"dijit/form/Button",
	"dijit/_TemplatedMixin",
	"dijit/_WidgetBase",
	"dijit/_WidgetsInTemplateMixin",
	"csi/_base/underscore",
	"csi/action",
	"csi/constants",
	"csi/def/Action",
	"csi/heading/_base",
	"csi/html",
	"csi/locale",
	"csi/state/rootStore",
	"csi/table/BatchFilterButtonAdd",
	"csi/types",
	"csi/view/_query/AdHocFilterContainer",
	"csi/widget/CheckedMultiSelectInDropdown",
	"csi/widget/DateRange",
	"csi/xml",
	"./MultiFilter",
	"dojo/text!./BatchFilter.html",

	"dijit/Tooltip",
	"dijit/form/Button",
	"dijit/form/ToggleButton",
	"csi/widget/CheckedMultiSelectDropdown",
	"csi/widget/DateRangeCalendar",
	"csi/widget/FilteringSelect",
], function(declare, lang, domClass, all, _Container, focusUtil, Button, _TemplatedMixin, _WidgetBase, _WidgetsInTemplateMixin,
	_, csiAction, constants, Action, csiHeading, html, locale, {localStore}, BatchFilterButtonAdd, types,
	AdHocFilterContainer, CheckedMultiSelectInDropdown, DateRange, xml, MultiFilter, template) {

	/** call callback function once view is loaded */
	function _whenViewLoaded(view, callback) {
		view.getAllDeferreds().then(() => {
			// do not call callback if view is no longer valid (destroyed)
			if (view.isValidView()) {
				callback();
			}
		})
	}

	return declare([MultiFilter], {
		templateString: template,

		// boolean - true if we processing ShowResult click - avoid updating pills at this time
		//bShowResultsChange: false,

		// button to add new filter
		//buttonAdd: BatchFilterButtonAdd

		// button to execute filtering query 
		//buttonsSeparator: DomNode - dijitToolbarSeparator

		// separator between filter pills and buttons
		//buttonShowResults: Button

		/**
		 * add material icon text to toolbar button "Filter"
		 * @override
		 */
		postCreate: function() {
			this.inherited(arguments);

			html.text(this._toggleButtonNode.iconNode, "filter_list");
		},

		/**
		 * Create filter container to display ad-hoc filters
		 * @param {DOMNode} domNode - container for filters
		 */
		createFilterContainer: function(domNode) {
			const {queryMixin} = this;
			let isInit = true;
			const refNode = domNode.classList.contains("csiViewToolbar") ? domNode : domNode.firstChild;

			const addButtons = (container) => {
				const buttonsContainer = container.buttonsContainerNode;
				this.buttonAdd = new BatchFilterButtonAdd({
					queryMixin,
					batchFilter: this,
					onChange: lang.hitch(this, this._onAddFilter),
				}).placeAt(buttonsContainer, "last");
				this.buttonsSeparator = html.create("span", {"class": "dijitToolbarSeparator"}, buttonsContainer, "last");
				this.buttonShowResults = new Button({
					"class": "csi-secondary-btn csi-show-results-btn",
					disabled: !queryMixin.suspended,
					label: locale.resource("BatchFiltersShowAllResults"),
					onClick: lang.hitch(this, this._onShowResultsClick),
				}).placeAt(buttonsContainer, "last");
				container.addClearAll();
				setClearAllVisibility(container);
			}
			const addPills = (container) => {
				const query = _.clone(queryMixin.get("query"));
				const pills = this.getPills();
				pills.forEach((pill) => {
					// if pill exists but has no corresponding query value - set pill value to "All" ([])
					const queryValue = query[pill.key] || [];
					const cms = pill.cms;
					const isDateRange = cms instanceof DateRange;
					if (isDateRange) {
						cms.set("value", queryValue);
					} else {
						cms.resetSelected(queryValue);
					}
					cms.updatePill();
					delete query[pill.key];
				});
				_.forEach(query, (item, key) => {
					container._addFilter(key);
				})
				// notify view of possible change to DOM structure/spacing
				queryMixin.resize?.();
			}
			const destroyPills = (pills) => {
				pills.forEach((pill) => {
					pill.destroyRecursive();
				});
				setClearAllVisibility(container);
			}
			const setClearAllVisibility = (container) => {
				html.setVisible(container.clearAll, this.getPills().length)
			}
			const updateToolbarButton = (container) => {
				// update toolbar plugin button styling
				const pills = this.getPills();
				const filtersCount = pills.length;
				const toolbarButton = this._toggleButtonNode;
				const countDiv = toolbarButton.countDiv;
				if (countDiv) {
					countDiv.remove();
				}
				domClass.toggle(toolbarButton.iconNode, "csi-batch-filter-active", filtersCount);
				if (filtersCount) {
					toolbarButton.containerNode.innerText = (filtersCount === 1) ? locale.resource("BatchFilter") : locale.resource("BatchFilters", [filtersCount]);
					toolbarButton.countDiv = html.create("span", {
						"class": "csi-batch-filter-btn-count-pill",
						innerText: filtersCount.toString(),
					}, toolbarButton.containerNode, "after");
				} else {
					toolbarButton.containerNode.innerText = locale.resource("Filter");
				}
			}

			const container = this.filterContainer = new AdHocFilterContainer({
				baseClass: "csi-batch-filter-container",
				"class": "dijitToolbar",
				bBatchFilter: true,
				queryMixin,
				onClearFilters: () => {
					const pills = this.getPills();

					if (pills.length === 0) {
						return;
					}

					pills.forEach(pill => {
						// update on column filters
						// this._filterButtons does not exists on gallery (and there are no columns and column filters)
						const button = this._filterButtons?.[pill.key]?.dropDown?.content;
						if (button) {
							button._lastValueReported = [];
						}
	
						pill.cms.store.reset()
					});
					this._resetFilter();
					// force saving filter as this might be missed when switching CVs
					queryMixin.saveAdHocFilters();
					destroyPills(pills);
					this.setAdHocEnabled();
					this.updateSuspendedCount();
					updateToolbarButton(container);
				},
				onClickFilter: (key, pillWidget) => {
					this._selectFilterValues(key, pillWidget);
				},
				onRemoveFilter: (key, pillWidget) => {
					// update view after removing filter
					if (this.getPills().length === 1) {
						container.onClearFilters();
						return;
					}
					if (pillWidget.cms) {
						pillWidget.cms.destroyRecursive();
					}
					pillWidget.destroyRecursive();
					setClearAllVisibility(container);
					this.setAdHocEnabled();
					this.updateSuspendedCount();
				},
				onAddFilter: (key, pillWidget) => {
					this._selectFilterValues(key, pillWidget);
					setClearAllVisibility(container);
					this.setAdHocEnabled();
				},
				_onQuery: () => {
					// cannot render filters if no query mixin or spec store
					if (!queryMixin || !queryMixin.get("querySpecStore")) {
						return;
					}
					updateToolbarButton(container);
					// button ShowResults clicked - to not update pills
					if (this.bShowResultsChange) {
						return;
					}
					// do not rebuild filters container if user refreshed widget while batch filters were changed but not yet applied
					const filtersApplied = this.buttonShowResults.disabled;
					if (!filtersApplied && !queryMixin.suspended) {
						_whenViewLoaded(queryMixin, () => {
							this.setAdHocEnabled();
						});
						return;
					}
					destroyPills(this.getPills());
					addPills(container);
					// delay is required as we want to allow query to start
					_.delay(lang.hitch(this, this.updateSuspendedCount), 250);
					updateToolbarButton(container);
					// show filter pane if user change ad hoc on column and view finished loading
					if (!isInit && !queryMixin.getDeferred()) {
						this._toggleFilter(true);
					}
				},
			}).placeAt(refNode, "after");
			const filterChecked = this.loadFilterCheckedState() || (queryMixin.suspended && !queryMixin.suspendedUntilScrolledIntoView);
			this._toggleFilter(filterChecked);
			queryMixin.watch("suspended", lang.hitch(this, this._onWatchSuspended));
			queryMixin.watch("showCountIfSuspended", lang.hitch(this, this._onWatchSuspended));
			queryMixin.watch("suspendedRequireFilter", lang.hitch(this, this._onWatchSuspended));
			addButtons(container);
			container.placementAnchor = this.buttonAdd;
			// allow view to start then add pills
			_whenViewLoaded(queryMixin, () => {
				addPills(container);
				if (!filterChecked && this.getPills().length) {
					this._toggleFilter(true);
				}
				isInit = false;
			});
		},

		/**
		 * open/close filter pane 
		  * @param {boolean} checked 
		 */
		_toggleFilter: function(checked) {
			this._toggleButtonNode.set("checked", checked);
			this._updateFilterChecked(checked);
		},

		/**
		 * button "Filter" click event handler: toggle visibility of filters panel 
		 */
		_toggleFilterClick: function() {
			this._updateFilterChecked(this._toggleButtonNode.checked);
		},

		/**
		 * show/hide container and save checked status
		 * @param {boolean} checked 
		 */
		_updateFilterChecked: function(checked) {
			this.filterContainer._setVisible(checked);
			this.saveFilterCheckedState();
		},

		/**
		 * allow adding filters without values
		 * @override
		 * @param {string} id 
		 * @param {Any[]} value 
		 */
		_onAddFilter: function(id) {
			// check if filter already exists to avoid adding duplicates
			const filterExists = _.filter(this.getPills(), (pill) => {
				return pill.key == id;
			});
			if (!filterExists.length) {
				// add filter
				this.filterContainer._addFilter(id);
			} else {
				// open existing filter
				const pillWidget = this.getPill(id);
				this.filterContainer.onAddFilter(id, pillWidget);
			}
		},

		/**
		 * show filters when table become suspended
		 * if table become unsuspended visibility of filters does not change
		 */
		_onWatchSuspended: function() {
			const {suspended, suspendedUntilScrolledIntoView} = this.queryMixin;

			if (suspended && !suspendedUntilScrolledIntoView) {
				this._toggleFilter(true);
			}

			// enable/disable ShowResults button
			this.setAdHocEnabled();
			this.updateSuspendedCount();
		},

		/**
		 * set label for button "Show Results"
		 * @param {number} count 
		 * @param {boolean} bShowAll 
		 */
		updateButtonShowResults: function(count, bShowAll) {
			const {buttonShowResults, queryMixin} = this;
			const {suspendedRequireFilter} = queryMixin;

			// remove old results count
			const countDiv = buttonShowResults.countDiv;
			if (countDiv) {
				countDiv.remove();
			}
			// we only supposed to show number of results if CV has "suspended" and "show count on suspend"
			if (bShowAll && !suspendedRequireFilter) {
				buttonShowResults.set("label", locale.resource("BatchFiltersShowAllResults"));
			} else {
				buttonShowResults.set("label", locale.resource("BatchFiltersShowResults"));
				if (count != null) {
					buttonShowResults.countDiv = html.create("span", {
						"class": "csi-batch-filter-btn-count-pill",
						innerText: count.toString(),
					}, buttonShowResults.containerNode, "after");
				}
			}
			const showButton = !buttonShowResults.disabled || (queryMixin.isSuspended() && suspendedRequireFilter);
			html.setVisible(buttonShowResults, showButton);
			this.updateSeparatorVisibility();
		},

		/**
		 * hide buttons separator if all buttons are hidden
		 */
		updateSeparatorVisibility: function() {
			const clearAllVisible = this.getPills().length > 0;
			const showResultsVisible = !this.buttonShowResults.disabled;
			html.setVisible(this.buttonsSeparator, clearAllVisible || showResultsVisible);
		},

		/**
		 * calculate number of results to show on "Show Results" button's pill if suspended and showCountIfSuspended
		 * remove button's pill if show count is not required anymore
		 */
		updateSuspendedCount: function() {
			const view = this.queryMixin;
			_whenViewLoaded(view, () => {
				let total;
				const batchFilters = this.getPills();
				const bShowAll = (batchFilters.length == 0);
				const filtersApplied = this.buttonShowResults.disabled;

				if (!filtersApplied && view.showCountIfSuspended && !view.suspendedUntilScrolledIntoView) {
					// generate query based on current batch filters
					const query = {};
					batchFilters.forEach((pill) => {
						const id = pill.key;
						let value = this.getPillValue(pill);
						if (value) {
							query[id] = value;
						}
					}, {});
					// make count request
					const store = view.get("store");
					if (_.isEqual(view.query, query)) {
						total = view.queryTotalCount;
					} else {
						var qr = view.getQueryResultByDomNode();
						queryOptions = {
							requestParameters: {
								"Operation": qr.getStore().requestOperationCount,
								"Qry.Limit.Filter": constants.QUERY_COUNT_LIMIT
							}
						};

						const dfd = store ? store.query(query, queryOptions) : qr.query(query, queryOptions);
						const dfds = [dfd];

						return all(dfds).then(
							results => {
								total = results[0].total;
								this.updateButtonShowResults(total, bShowAll);
							});
					}
				}
				this.updateButtonShowResults(total, bShowAll);
			});
		},

		/**
		 * get value of pill 
		 * @param {Widget} pill
		 * @return {string[] | number[]}
		 */
		getPillValue(pill) {
			const {cms} = pill;
			if (cms instanceof DateRange) {
				return cms.dropDown.value;
			} else {
				return cms.value;
			}
		},

		/**
		 * create and open CMS allowing to choose ad-hoc filter values
		 * @param {string} selectedColumn 
		 * @param {NameValuePair} pillWidget 
		 */
		_selectFilterValues: function(selectedColumn, pillWidget) {
			const updateSuspendedCount = _.debounce(lang.hitch(this, this.updateSuspendedCount), 250);
			let isDateRange;
			// apply key pressed effect to the pill
			const toggleDropdownOpenClass = (name, oldValue, newValue) => {
				pillWidget.domNode.classList.toggle("csi-dropdown-open", newValue);
			}
			// update pill label and close drop down
			const updatePill = () => {
				const cms = pillWidget.cms;
				const value = this.getPillValue(pillWidget);
				let displayValue;
				// update pill value
				const valLength = value.length;
				if ((valLength == 0) || ((valLength == 1) && (value[0] === ""))) {
					displayValue = locale.resource("SelectAll");
				} else if (isDateRange) {
					displayValue = types.formatDateRange(value);
				} else {
					// show first non empty selection and number of other selections (mini pill "+N")
					const value0 = value[0] || ((value.length > 1) && value[1]) || "";
					if ((value0 === xml.VALUE_BLANK) || (value0 === xml.VALUE_NONBLANK)) {
						displayValue = locale.resource(value0);
					} else {
						// check to see if value require localization (enum value for instance)
						const item = cms.store.get(value0);
						if (cms.store.spec.type == xml.ATTR_ENUM) {
							displayValue = locale.getEnumName(value0);
						} else {
							displayValue = item?._text || value0;
						}
					}
				}
				pillWidget.set("value", displayValue);
				pillWidget.cmsValue = value;
				// synchronization between ad hoc and batch filters. Call this function for controls which have it
				pillWidget.cms.reinitializeValue?.(value);

				// create mini pill for number of selection above first one
				const countDiv = pillWidget.countDiv;
				if (countDiv) {
					countDiv.remove();
				}
				if (!isDateRange && value.length > 1) {
					const count = value.length - 1;
					// create filter count pill
					pillWidget.countDiv = html.create("span", {
						"class": "csi-batch-filter-count",
						innerText: count,
					}, pillWidget.valueNode, "after");
					// create large PLUS symbol
					html.create("span", {
						innerText: "+",
					}, pillWidget.countDiv, "first");
				}
			}
			// update pill and close drop down
			const closeDropDown = () => {
				this.setAdHocEnabled();
				const cms = pillWidget.cms;
				if (!cms.bPasting) {
					const oldValue = pillWidget.cmsValue;
					updatePill();
					const newValue = pillWidget.cmsValue;

					if (oldValue !== newValue) {
						updateSuspendedCount();
					}
				}

				// drop down already destroyed
				if (!cms.dropDown) {
					return;
				}

				// destroy and close drop down. HAve to destroy as it is getting updated from multiple sources
				cms.closeDropDown(false);
				if (!isDateRange) {
					cms.dropDown.destroyRecursive();
					cms.textbox.value = "";
					delete cms.dropDown;
				}
			}
			// create drop down with onBlur handler
			const createDropDown = () => {
				const valueStore = this.queryMixin.getQueryValueStore(selectedColumn);
				const pillDomNode = pillWidget.domNode;
				const spec = valueStore.spec;
				const queryValue = this.queryMixin.query[selectedColumn];
				isDateRange = spec.isDateAttribute();
				let params = {};

				if (isDateRange) {
					const ren = csiHeading.getHeadingRenderer(null, spec.heading);
					params = ren.getDateRangeParams(spec);
					if (queryValue) {
						params.value = queryValue;
					}
				}

				const widgetClass = isDateRange ? DateRange : CheckedMultiSelectInDropdown;
				const cms = pillWidget.cms = widgetClass({
					addNewItem: spec.canFilterByOperator(xml.OP_MATCHES),
					defaultClearText: "SelectAll",
					store: valueStore,

					_onBlur: () => {
						const newFocus = document.getElementById(focusUtil.activeStack.last());
						if (pillDomNode != newFocus) {
							closeDropDown();
						}
					},
					updatePill: () => {
						updatePill();
					},
					onEscape: function() {
						closeDropDown();
					},
					...params,
				}).placeAt(pillDomNode, "first");

				// if widget created by query change rather then batch filter interaction - do not open popup and update pill
				cms.watch("_opened", toggleDropdownOpenClass);
				if (isDateRange) {
					cms.dropDown.bBatchFilter = true;
					// do not open dropdown as pill created after column ad hoc change (queryValue is not empty)
					if (!queryValue) {
						// for date range we need to open drop down as it will not auto open like for CMS In Dropdown.
						cms.openDropDown();
					}
				} else if (queryValue) {
					cms.resetSelected(queryValue);
					cms.reinitializeValue(queryValue);
					closeDropDown();
				}
			};

			// create or toggle drop down 
			const cms = pillWidget.cms;
			if (!cms) {
				createDropDown();
			} else {
				isDateRange = cms instanceof DateRange;
				if (cms._opened) {
					closeDropDown();
				} else {
					if (isDateRange) {
						cms.openDropDown();
					} else {
						// store reset and set store are required to synchronize batch filter cms text numbers with current view situation 
						this.queryMixin._resetStores();
						
						// when user already hits "showResults", then query/queryForServer is already populated, need to ignore the selected ones to avoid adding duplicated entries
						const valuesStore = this.queryMixin.getQueryValueStore(selectedColumn, true);
						cms.set("store", valuesStore);
						
						cms.loadAndOpenDropDown();
					}
				}
			}
		},

		/**
		 * enable/disable column adhoc filters and "Show Results" button depend on in batch filter differ from query
		 * @param {*} forceEnable - true to skip the check and just enable adhoc buttons 
		 */
		setAdHocEnabled: function(forceEnable) {
			// find if batch filters differ from current query filters
			const queryMixin = this.queryMixin
			const {suspended} = queryMixin;
			const query = {...queryMixin.get("query")}
			// update query and on columns filters
			let dirty = false;
			const filters = this.getPills();
			if (!forceEnable) {
				dirty = filters.some((pill) => {
					const {cms, key} = pill;

					// solve problem comparing boolean to "false" or "true" step 1
					const value = cms.getSelectedItems ?
						cms.getSelectedItems().map((item) => item._value) :
						this.getPillValue(pill);

					const batchValue = value?.length ? value : [];
					const queryValue = query[key] || [];
					const spec = queryMixin.getSpec(key);

					delete query[key];

					return !spec.compareQueryValues(batchValue, queryValue);
				});

				// check if query has filters which are not in batch filter
				dirty = dirty || Object.keys(query).length;
			}
			if (this._filterButtons) {
				_.forEach(this._filterButtons, (button) => {
					if (button && (!button._destroyed)) {
						button.set("disabled", dirty);
					}
				});
			}

			// do not allow to change columns layout until batch filter gets applied;
			// do not allow plugin to be enabled if view is still disabled
			queryMixin.getPlugin("columns")?.set("disabled", !!dirty || queryMixin.disabled);

			const {suspendedRequireFilter} = queryMixin;
			const noPillValue = filters.every((pill) => !this.getPillValue(pill)?.length);

			this.buttonShowResults.set("disabled", !!((!dirty && !suspended) || (suspendedRequireFilter && noPillValue)));

			_whenViewLoaded(this.queryMixin, () => {
				if (suspended && suspendedRequireFilter && (this.getPills().length == 0)) {
					this.buttonAdd.openDropdown();
				}
			});
		},

		/**
		 * button "Show Results" on click
		 * apply added and remove deleted batch filters
		 */
		_onShowResultsClick: function() {
			// prevent multi filter internal updates
			this._bInternalChange = true;
			// prevent batch filter internal updates
			this.bShowResultsChange = true;
			// remember old query
			const queryMixin = this.queryMixin;
			const query = _.clone(queryMixin.get("query"));
			// update query and on columns filters
			this.getPills().forEach((pill) => {
				const id = pill.key;
				let value = this.getPillValue(pill);
				value = (value && value.length) ? value : [];
				this._changeFilter(id, value);
				this._onChangeFilter(id, value);
				delete query[id]
			}, {});
			// remove deleted filters from query
			_.forEach(query, (item, key) => {
				this._changeFilter(key, []);
				this._onChangeFilter(key, []);
			});
			this.setAdHocEnabled(true);
			this.updateSuspendedCount();
			this.bShowResultsChange = false;
			this._bInternalChange = false;

			if (queryMixin.get("suspended")) {
				_.delay(() => {
					const action = lang.mixin(new Action(), {
						m_id: "TableSuspend",
					});
					csiAction.invoke(queryMixin, null, action, {
						actionValue: false,
					});
				}, 200); // we need 200ms for switch button animation to complete;
			}
		},

		/** 
		 * return pill widget
		 * @param {string} key - key of the filter to return
		 * @return {Widget} - pill widget
		 */
		getPill: function(key) {
			return this.filterContainer.getChildren().find(pill => pill.key == key);
		},
		/** 
		 * return list of pill widgets but ignore buttons 
		 * @return {Widget}
		 */
		getPills: function() {
			return _.filter(this.filterContainer.getChildren(), (pill) => {
				return pill.key != null;
			});
		},

		getViewContext: function() {
			return this.queryMixin.get("customViewContext") || "common";
		},

		/**
		 * save state of toolbar "Filter" toggle button
		 */
		saveFilterCheckedState: function() {
			const {setBatchFiltersOpen} = localStore;
			setBatchFiltersOpen(this.getViewContext(), this._toggleButtonNode.checked);
		},

		/**
		 * load and apply state of toolbar "Filter" toggle button
		 * @returns {boolean} - true for "filter open"
		 */
		loadFilterCheckedState: function() {
			return !!localStore.batchFiltersOpen[this.getViewContext()];
		},
	});
});
